import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { queryKeys } from '../../../lib/queryKeys.js';
import { toChatMessages } from '../chatHelpers.js';
import {
  continueSharedConversation,
  getSharedWithMe,
  hideSharedConversation,
  readSharedConversation,
} from './shareClient.js';

/** The caller's Shared with me list, and hiding one from it. */
export const useSharedWithMe = (enabled: boolean) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { shareId: openShareId } = useParams<{ shareId: string }>();
  const list = useQuery({ queryKey: queryKeys.sharedWithMe(), queryFn: getSharedWithMe, enabled });
  const hide = useMutation({
    mutationFn: (shareId: string) => hideSharedConversation(shareId),
    onSuccess: (_done, shareId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.sharedWithMe() });
      if (shareId === openShareId) navigate('/');
    },
  });
  return { list, hide };
};

/** One shared conversation (marked opened on read) and Continue in my chat. */
export const useSharedConversationLogic = (shareId: string) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const read = useQuery({
    queryKey: queryKeys.sharedConversation(shareId),
    queryFn: async () => {
      const result = await readSharedConversation(shareId);
      // Reading marked it opened: the badge and dot clear.
      void qc.invalidateQueries({ queryKey: queryKeys.sharedWithMe() });
      return { share: result.share, messages: toChatMessages(result.messages) };
    },
  });
  const continueInMyChat = useMutation({
    mutationFn: () => continueSharedConversation(shareId),
    onSuccess: (conversationId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.agentThreadsRoot() });
      navigate(`/chat/${conversationId}`);
    },
  });
  return { read, continueInMyChat };
};
